import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useAnimationControls, AnimatePresence } from 'framer-motion';
import { Star, CheckCircle, X, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TEXT_REVIEWS = [
  { name: 'Nour A.', location: 'Cairo', rating: 5, text: 'Absolutely exceeded my expectations. Quality is stunning and delivery was incredibly fast.', product: 'Premium Collection', date: '2 days ago' },
  { name: 'Ahmed K.', location: 'Alexandria', rating: 5, text: 'The product blew me away. Packaging was pristine and the item looks even better in person.', product: 'Lifestyle Series', date: '5 days ago' },
  { name: 'Sara M.', location: 'Giza', rating: 5, text: "Best online shopping experience I've had in Egypt. Customer support was responsive and product arrived perfectly.", product: 'Ziko Exclusive', date: '1 week ago' },
  { name: 'Omar F.', location: 'Cairo', rating: 5, text: 'Third purchase and still impressed. You can tell this brand genuinely cares about quality.', product: 'Premium Collection', date: '1 week ago' },
  { name: 'Layla H.', location: 'Sharm', rating: 5, text: 'Ordered as a gift — my friend was completely stunned. Truly a premium experience.', product: 'Gift Selection', date: '2 weeks ago' },
  { name: 'Karim R.', location: 'Cairo', rating: 5, text: "Fast, reliable, genuinely high quality. The product photos don't even do it justice.", product: 'Lifestyle Series', date: '2 weeks ago' },
  { name: 'Mona T.', location: 'Luxor', rating: 5, text: 'Found my go-to store for premium items. The vibe, the quality, the service — all perfect.', product: 'Ziko Exclusive', date: '3 weeks ago' },
];

const CARD_W   = 175;
const CARD_GAP = 16;
const UNIT     = CARD_W + CARD_GAP;
const DURATION = 52;

/* ── Text review card ────────────────────────────────────────────── */
function TextCard({ review }: { review: typeof TEXT_REVIEWS[0] }) {
  return (
    <motion.div whileHover={{ scale: 1.04, y: -6 }} transition={{ type: 'spring', stiffness: 320, damping: 22 }}
      className="flex-shrink-0" style={{ width: CARD_W }}>
      <div className="glass rounded-xl p-3 flex flex-col gap-2 select-none border border-border/40" style={{ width: CARD_W, height: CARD_W, overflow: 'hidden' }}>
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className={`h-3 w-3 ${i < review.rating ? 'fill-chart-3 text-chart-3' : 'text-muted-foreground/20'}`} />
          ))}
        </div>
        <div className="text-primary/25 font-display text-4xl leading-none -mb-1 select-none">"</div>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-5">{review.text}</p>
        <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-border/50">
          <div>
            <p className="text-[11px] font-semibold text-foreground">{review.name}</p>
            <p className="text-[10px] text-muted-foreground">{review.location} · {review.date}</p>
          </div>
          <span className="text-[10px] font-medium text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded-full whitespace-nowrap">{review.product}</span>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Review submission form ──────────────────────────────────────── */
function ReviewSubmitForm() {
  const [orderNumber,    setOrderNumber]    = useState('');
  const [customerName,   setCustomerName]   = useState('');
  const [reviewText,     setReviewText]     = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [previewUrl,     setPreviewUrl]     = useState<string | null>(null);
  const [consented,      setConsented]      = useState(false);
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [isVerifying,    setIsVerifying]    = useState(false);
  const [submitted,      setSubmitted]      = useState(false);

  // Order verification state
  const [orderVerified,   setOrderVerified]   = useState(false);
  const [orderError,      setOrderError]      = useState('');
  const [verifiedOrder,   setVerifiedOrder]   = useState<{ order_number: string; customer_name: string } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScreenshotFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  // Verify order number exists AND hasn't already had a review submitted
  const verifyOrder = async () => {
    const num = orderNumber.trim().toUpperCase();
    if (!num) { setOrderError('Please enter your order number.'); return; }
    setIsVerifying(true);
    setOrderError('');
    setOrderVerified(false);
    setVerifiedOrder(null);
    try {
      // 1. Check order exists — via the curated RPC (no raw PII select)
      const { data: order } = await supabase
        .rpc('get_order_by_number', { p_order_number: num, p_email: null })
        .maybeSingle();

      if (!order) {
        setOrderError('Order not found. Please check your order number (e.g. ZKO-ABC123).');
        return;
      }

      // 2. Check no review already submitted for this order
      const { data: existingReviews } = await supabase
        .from('customer_reviews')
        .select('id')
        .eq('order_number', num)
        .limit(1);

      if (existingReviews && existingReviews.length > 0) {
        setOrderError('A review has already been submitted for this order. Only one review per order is allowed.');
        return;
      }

      // Order verified, pre-fill name if available
      setVerifiedOrder(order);
      setOrderVerified(true);
      if (order.customer_name && !customerName) {
        setCustomerName(order.customer_name.includes('@') ? '' : order.customer_name);
      }
    } catch (err: any) {
      setOrderError('Could not verify order. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!orderVerified) { toast.error('Please verify your order number first.'); return; }
    if (!customerName.trim() || !reviewText.trim()) { toast.error('Please fill in your name and review text.'); return; }
    if (!consented) { toast.error('Please tick the consent checkbox before submitting.'); return; }
    setIsSubmitting(true);
    try {
      let screenshotUrl = '';
      if (screenshotFile) {
        const ext = screenshotFile.name.split('.').pop();
        const fileName = `review-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from('review-screenshots')
          .upload(fileName, screenshotFile, { cacheControl: '3600', upsert: false });
        if (!uploadErr) {
          const { data: urlData } = supabase.storage.from('review-screenshots').getPublicUrl(fileName);
          screenshotUrl = urlData.publicUrl;
        }
      }

      const { error: dbErr } = await supabase.from('customer_reviews').insert([{
        customer_name:  customerName.trim(),
        order_number:   orderNumber.trim().toUpperCase(),
        review_text:    reviewText.trim(),
        screenshot_url: screenshotUrl || null,
        status:         'pending',
        consented:      true,
      }]);

      if (dbErr) throw dbErr;
      setSubmitted(true);
    } catch (err: any) {
      toast.error('Submission failed: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <div className="h-14 w-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <CheckCircle className="h-7 w-7 text-emerald-400" />
        </div>
        <div className="space-y-1.5 max-w-sm">
          <h3 className="font-bold text-lg">Thank you, {customerName}! 💛</h3>
          <p className="text-sm text-muted-foreground">
            We've received your review and will verify it within 24–48 hours. Once approved, it will appear on our Wall of Love. We truly appreciate you taking the time — it means the world to us!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Step 1: Verify order */}
      <div className="space-y-2 p-4 rounded-xl border border-border bg-secondary/10">
        <p className="text-xs font-semibold text-foreground uppercase tracking-wider">Step 1 — Verify Your Order</p>
        <p className="text-xs text-muted-foreground">Enter your ZIKO order number to verify your purchase before submitting a review.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={orderNumber}
            onChange={e => { setOrderNumber(e.target.value.toUpperCase()); setOrderError(''); setOrderVerified(false); setVerifiedOrder(null); }}
            placeholder="e.g. ZKO-1A2B3C"
            disabled={orderVerified}
            className={`flex-1 px-3 py-2 rounded-lg bg-secondary/50 border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors disabled:opacity-60 ${orderError ? 'border-destructive' : orderVerified ? 'border-emerald-500' : 'border-border'}`}
          />
          {orderVerified ? (
            <button onClick={() => { setOrderVerified(false); setVerifiedOrder(null); setOrderNumber(''); }}
              className="px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:bg-secondary transition-colors">
              Change
            </button>
          ) : (
            <button onClick={verifyOrder} disabled={!orderNumber.trim() || isVerifying}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 flex items-center gap-1.5">
              {isVerifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Verify'}
            </button>
          )}
        </div>
        {orderError && (
          <div className="flex items-start gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            {orderError}
          </div>
        )}
        {orderVerified && verifiedOrder && (
          <div className="flex items-center gap-1.5 text-xs text-emerald-400">
            <CheckCircle className="h-3.5 w-3.5 shrink-0" />
            Order verified ✓ You're now eligible to submit a review.
          </div>
        )}
      </div>

      {/* Step 2: Review form — only shown after order verified */}
      <AnimatePresence>
        {orderVerified && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden space-y-4">

            <p className="text-xs font-semibold text-foreground uppercase tracking-wider pt-2">Step 2 — Your Review</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Your Name <span className="text-destructive">*</span></label>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
                  placeholder="e.g. Sara M."
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50" />
              </div>
              <div className="flex items-end">
                <div className="text-xs text-muted-foreground px-3 py-2 rounded-lg bg-primary/5 border border-primary/15 w-full">
                  Order: <span className="font-mono font-semibold text-foreground">{orderNumber}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Your Review <span className="text-destructive">*</span></label>
              <textarea value={reviewText} onChange={e => setReviewText(e.target.value)}
                placeholder="Tell us about your experience with ZIKO…"
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none" />
            </div>

            {/* Screenshot */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Screenshot <span className="text-muted-foreground/50">(optional — photo of your order or product)</span>
              </label>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
              {previewUrl ? (
                <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-border">
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                  <button onClick={() => { setScreenshotFile(null); setPreviewUrl(null); }}
                    className="absolute top-1 right-1 p-0.5 rounded-full bg-background/80 border border-border">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors">
                  <ImageIcon className="h-4 w-4" /> Upload screenshot
                </button>
              )}
            </div>

            {/* Consent */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5 shrink-0">
                <input type="checkbox" checked={consented} onChange={e => setConsented(e.target.checked)} className="sr-only" />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${consented ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'}`}>
                  {consented && <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">I consent and confirm</span> that my input, name, and any attached photo are 100% authentic and represent my genuine experience. I agree to ZIKO's{' '}
                <a href="/policies" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">review disclosure policy</a>{' '}
                and grant ZIKO permission to display my review publicly after verification.
              </p>
            </label>

            <button onClick={handleSubmit}
              disabled={isSubmitting || !consented || !customerName.trim() || !reviewText.trim()}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50">
              {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Submit Review</>}
            </button>

            <p className="text-xs text-muted-foreground">All reviews are manually verified before publishing. You can only submit one review per order.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
export default function CustomerFeedback() {
  const [paused, setPaused] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const controls = useAnimationControls();

  const TRACK = [...TEXT_REVIEWS, ...TEXT_REVIEWS];
  const trackWidth = TEXT_REVIEWS.length * UNIT;

  const startScroll = useCallback(() => {
    controls.start({
      x: [0, -trackWidth],
      transition: { duration: DURATION, ease: 'linear', repeat: Infinity, repeatType: 'loop' },
    });
  }, [controls, trackWidth]);

  useEffect(() => { if (trackWidth > 0) startScroll(); }, [startScroll, trackWidth]);

  return (
    <>
      <section className="pt-4 pb-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-2">Verified Customers</p>
          <h2 className="font-display text-xl font-bold">Wall of Love 💛</h2>
          <p className="text-sm text-muted-foreground mt-2">Real stories from real customers — every review is verified by our team</p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="h-px w-16 bg-border" />
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-3 w-3 fill-chart-3 text-chart-3" />)}</div>
            <div className="h-px w-16 bg-border" />
          </div>
        </div>

        {/* Carousel */}
        <div className="relative cursor-default"
          onMouseEnter={() => { setPaused(true); controls.stop(); }}
          onMouseLeave={() => { setPaused(false); startScroll(); }}>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-background to-transparent" />
          <motion.div animate={controls} className="flex gap-4 pl-4 items-start" style={{ width: 'max-content' }}>
            {TRACK.map((review, i) => <TextCard key={`t-${i}`} review={review} />)}
          </motion.div>

        </div>

        {/* Submit review panel */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <div className="glass rounded-2xl border border-border p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-bold">Share Your Experience</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Your review gets featured here after our team verifies it. Order number required.</p>
              </div>
              <button onClick={() => setShowForm(v => !v)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary border border-primary/20 text-sm font-semibold hover:bg-primary/20 transition-colors shrink-0 ms-4">
                {showForm ? 'Close' : '✍️ Write a Review'}
              </button>
            </div>
            {showForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} className="overflow-hidden border-t border-border pt-6">
                <ReviewSubmitForm />
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="h-px w-8 bg-primary/30" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent via-border to-transparent" />
        </div>
      </div>
    </>
  );
}



